import {
  required,
  maxLength,
  between,
  requiredIf,
  or,
} from 'vuelidate/lib/validators';
import {
  HumidityCurve,
  LoadSimulationCoolingCommand,
  ReferenceHumidity,
  HumiditySource,
} from 'sigma-mappings';
import { isBetween as dateTimeIsBetween } from '@/services/dateTime/datetimeService';

export const validateValue = (val1, val2) => (val2 ? val1 <= val2 : true);

const oilInitialTemperature = fields => ({
  required,
  maxLength: maxLength(fields.oilInitialTemperature.maxLength),
  between: between(
    fields.oilInitialTemperature.minValue,
    fields.oilInitialTemperature.maxValue,
  ),
});

const windingInitialTemperature = fields => ({
  required,
  maxLength: maxLength(fields.windingInitialTemperature.maxLength),
  between: between(
    fields.windingInitialTemperature.minValue,
    fields.windingInitialTemperature.maxValue,
  ),
});

const windingTimeConstant = fields => ({
  required,
  between: between(
    fields.windingTimeConstant.minValue,
    fields.windingTimeConstant.maxValue,
  ),
});

const windingHotspotFactor = fields => ({
  required,
  maxLength: maxLength(fields.windingHotspotFactor.maxLength),
  between: between(
    fields.windingHotspotFactor.minValue,
    fields.windingHotspotFactor.maxValue,
  ),
});

const simulationInitialDate = fields => ({
  required,
  between: dateTimeIsBetween(
    fields.simulationInitialDate.minValue,
    fields.simulationInitialDate.maxValue,
  ),
});

const simulationInitialTime = () => ({
  required,
});

const considerHumidity = () => ({
  required,
});

const paperHumidity = (fields, values) => ({
  requiredIf: requiredIf(() => values.paperHumiditySource === HumiditySource.CUSTOM),
  between: or(
    between(
      fields.paperHumidity.minValue,
      fields.paperHumidity.maxValue,
    ),
    () => values.paperHumiditySource !== HumiditySource.CUSTOM,
  ),
});

const referenceHumidity = (fields, values) => ({
  requiredIf: requiredIf(() => values.referenceHumidityBase === ReferenceHumidity.CUSTOM),
  between: or(
    between(
      fields.referenceHumidity.minValue,
      fields.referenceHumidity.maxValue,
    ),
    () => values.referenceHumidityBase !== ReferenceHumidity.CUSTOM,
  ),
});

const oilOxygen = (fields, values) => ({
  requiredIf: requiredIf(() => values.methodForHumidityCurve === HumidityCurve.OXYGEN_DAN_MARTIN),
  between: or(
    between(
      fields.oilOxygen.minValue,
      fields.oilOxygen.maxValue,
    ),
    () => values.methodForHumidityCurve !== HumidityCurve.OXYGEN_DAN_MARTIN,
  ),
});

const coolingCommand = () => ({
  required,
});

const stage1 = (fields, stageValue) => ({
  requiredIf: requiredIf(() => (stageValue.coolingState === LoadSimulationCoolingCommand.AUTO)
    && stageValue.forcedStageCoolingQuantity >= 1),
  between: or(
    between(
      fields.stage1.minValue,
      fields.stage1.maxValue,
    ),
    () => stageValue.coolingState !== LoadSimulationCoolingCommand.AUTO
      || stageValue.forcedStageCoolingQuantity === 0,
  ),
});

const stage2 = (fields, stageValue) => ({
  requiredIf: requiredIf(() => (stageValue.coolingState === LoadSimulationCoolingCommand.AUTO)
    && stageValue.forcedStageCoolingQuantity >= 2),
  between: or(
    between(
      fields.stage2.minValue,
      fields.stage2.maxValue,
    ),
    () => stageValue.coolingState !== LoadSimulationCoolingCommand.AUTO
      || stageValue.forcedStageCoolingQuantity === 0,
  ),
  lessThanLast: () => stageValue.coolingState !== LoadSimulationCoolingCommand.AUTO
    || validateValue(
      stageValue.stage1,
      stageValue.stage2,
    ),
});

const stage3 = (fields, stageValue) => ({
  requiredIf: requiredIf(() => (stageValue.coolingState === LoadSimulationCoolingCommand.AUTO)
    && stageValue.forcedStageCoolingQuantity >= 3),
  between: or(
    between(
      fields.stage3.minValue,
      fields.stage3.maxValue,
    ),
    () => stageValue.coolingState !== LoadSimulationCoolingCommand.AUTO
      || stageValue.forcedStageCoolingQuantity === 0,
  ),
  lessThanLast: () => stageValue.coolingState !== LoadSimulationCoolingCommand.AUTO
    || validateValue(
      stageValue.stage2,
      stageValue.stage3,
    ),
});

const stage4 = (fields, stageValue) => ({
  requiredIf: requiredIf(() => (stageValue.coolingState === LoadSimulationCoolingCommand.AUTO)
    && stageValue.forcedStageCoolingQuantity >= 4),
  between: or(
    between(
      fields.stage4.minValue,
      fields.stage4.maxValue,
    ),
    () => stageValue.coolingState !== LoadSimulationCoolingCommand.AUTO
      || stageValue.forcedStageCoolingQuantity === 0,
  ),
  lessThanLast: () => stageValue.coolingState !== LoadSimulationCoolingCommand.AUTO
    || validateValue(
      stageValue.stage3,
      stageValue.stage4,
    ),
});

const hysteresisCoolingShutdown = (fields, stageValue) => ({
  requiredIf: requiredIf(() => (stageValue.coolingState === LoadSimulationCoolingCommand.AUTO)
    && stageValue.forcedStageCoolingQuantity > 0),
  between: or(
    between(
      fields.hysteresisCoolingShutdown.minValue,
      fields.hysteresisCoolingShutdown.maxValue,
    ),
    () => stageValue.coolingState !== LoadSimulationCoolingCommand.AUTO
      || stageValue.forcedStageCoolingQuantity === 0,
  ),
});

const oilAlarmTemperature = fields => ({
  required,
  between: between(
    fields.oilAlarmTemperature.minValue,
    fields.oilAlarmTemperature.maxValue,
  ),
});

const oilShutdownTemperature = fields => ({
  required,
  between: between(
    fields.oilShutdownTemperature.minValue,
    fields.oilShutdownTemperature.maxValue,
  ),
});

const windingAlarmTemperature = fields => ({
  required,
  between: between(
    fields.windingAlarmTemperature.minValue,
    fields.windingAlarmTemperature.maxValue,
  ),
});

const windingShutdownTemperature = fields => ({
  required,
  between: between(
    fields.windingShutdownTemperature.minValue,
    fields.windingShutdownTemperature.maxValue,
  ),
});

const normEndLifeCriteria = () => ({
  required,
});

const normTemperatureCalculation = () => ({
  required,
});

export default (fields, values) => ({
  conditions: {
    oilInitialTemperature: oilInitialTemperature(fields),
    windingInitialTemperature: windingInitialTemperature(fields),
    windingTimeConstant: windingTimeConstant(fields),
    windingHotspotFactor: windingHotspotFactor(fields),
    simulationInitialDate: simulationInitialDate(fields),
    simulationInitialTime: simulationInitialTime(),
    considerHumidity: considerHumidity(),
    paperHumidity: paperHumidity(fields, values),
    referenceHumidity: referenceHumidity(fields, values),
    oilOxygen: oilOxygen(fields, values),
    coolingCommand: coolingCommand(),
    stage1: stage1(fields, values),
    stage2: stage2(fields, values),
    stage3: stage3(fields, values),
    stage4: stage4(fields, values),
    hysteresisCoolingShutdown: hysteresisCoolingShutdown(fields, values),
    oilAlarmTemperature: oilAlarmTemperature(fields),
    oilShutdownTemperature: oilShutdownTemperature(fields),
    windingAlarmTemperature: windingAlarmTemperature(fields),
    windingShutdownTemperature: windingShutdownTemperature(fields),
    normEndLifeCriteria: normEndLifeCriteria(),
    normTemperatureCalculation: normTemperatureCalculation(),
  },
});
